import { useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhotoInput from "@/components/PhotoInput";
import NextSeasonDialog from "@/components/NextSeasonDialog";

export default function Settings() {
  const { session, setClub } = useAuth();
  const club = session.club;
  const [f, setF] = useState({ name: club.name, logo_path: club.logo_path, season: club.season, manager_name: club.manager_name || "", manager_phone: club.manager_phone || "", manager_email: club.manager_email || "" });
  const [seasonOpen, setSeasonOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.put("/club", f); setClub(data); toast.success("Paramètres enregistrés"); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const resetDemo = async () => {
    if (!window.confirm("Réinitialiser toutes les données de démonstration ?")) return;
    try { await api.post("/demo/reset"); const { data } = await api.get("/club"); setClub(data); setF({ name: data.name, logo_path: data.logo_path, season: data.season, manager_name: data.manager_name || "", manager_phone: data.manager_phone || "", manager_email: data.manager_email || "" }); toast.success("Données de démo réinitialisées"); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="max-w-[720px]" data-testid="settings-page">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Paramètres du club</h1>
      <p className="text-sm text-[#5B665F] mt-2">Ces informations apparaissent sur vos bilans de visibilité.</p>
      <form onSubmit={save} className="card p-5 sm:p-7 mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 fade-up">
        <label className="block sm:col-span-2"><span className="field-label">Nom du club</span><input className="field" required value={f.name} onChange={set("name")} data-testid="settings-name-input" /></label>
        <div className="sm:col-span-2"><PhotoInput label="Logo du club" value={f.logo_path} onChange={(p) => setF({ ...f, logo_path: p })} testId="settings-logo" /></div>
        <label className="block"><span className="field-label">Saison en cours</span><input className="field" required placeholder="2026-2027" value={f.season} onChange={set("season")} data-testid="settings-season-input" /></label>
        <p className="text-xs text-[#5B665F] self-end pb-3">Modifier ce champ renomme la saison en cours. Pour en démarrer une nouvelle, utilisez « Saison suivante » ci-dessous.</p>
        <label className="block"><span className="field-label">Responsable partenariats</span><input className="field" value={f.manager_name} onChange={set("manager_name")} data-testid="settings-manager-name-input" /></label>
        <label className="block"><span className="field-label">Téléphone du responsable</span><input className="field" type="tel" value={f.manager_phone} onChange={set("manager_phone")} data-testid="settings-manager-phone-input" /></label>
        <label className="block sm:col-span-2"><span className="field-label">Email du responsable (les sponsors répondront à cette adresse)</span><input className="field" type="email" placeholder={session.user.email} value={f.manager_email} onChange={set("manager_email")} data-testid="settings-manager-email-input" /></label>
        <div className="sm:col-span-2"><button className="btn-primary" disabled={busy} data-testid="settings-save-button">{busy ? "Enregistrement…" : "Enregistrer"}</button></div>
      </form>
      <section className="card p-5 sm:p-7 mt-6" data-testid="next-season-section">
        <h2 className="text-lg font-bold">Saison suivante</h2>
        <p className="text-sm text-[#5B665F] mt-1">Archive la saison {club.season} (toujours consultable depuis la page Sponsors) et reconduit les sponsors renouvelés.</p>
        <button className="btn-primary mt-4" onClick={() => setSeasonOpen(true)} data-testid="next-season-button">Démarrer la saison suivante</button>
      </section>
      <NextSeasonDialog open={seasonOpen} onOpenChange={setSeasonOpen} club={club} onDone={(c) => { setClub(c); setF((x) => ({ ...x, season: c.season })); }} />
      {club.is_demo && (
        <section className="card p-5 sm:p-7 mt-6" data-testid="demo-reset-section">
          <h2 className="text-lg font-bold">Compte de démonstration</h2>
          <p className="text-sm text-[#5B665F] mt-1">Remet le club US Calanques et ses six sponsors dans leur état initial.</p>
          <button className="btn-outline mt-4" onClick={resetDemo} data-testid="demo-reset-button"><RotateCcw size={15} /> Réinitialiser les données de démo</button>
        </section>
      )}
    </div>
  );
}
